import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Select, notification } from 'antd'
import {
    AppstoreOutlined,
    CalendarOutlined,
    CheckOutlined,
    CloseOutlined,
    EnvironmentOutlined,
    GlobalOutlined,
    TrophyOutlined,
} from '@ant-design/icons'

import { Constants } from '@/utils'
import TranslationNS from '@/utils/Constants/TranslationNS'
import { convertToSlashDateTimeFormat } from '@/utils/DateHelpers'
import { EventServices, QualificationsServices } from '@/middlewares/services'
import DateTimeInput from '@/components/common/Tz/DateTimeInput'
import TimezoneSelect from '@/components/common/TimezoneSelect'
import CountryInput from '@/components/common/CountryInput'
import AddressAutoCompleteInput from '@/components/common/AddressAutoCompleteInput'
import { useOrganizerShell } from '@/components/Organizer/OrganizerShell/OrganizerShellContext'
import { useEventHub } from '../EventHubContext'
import EventHubSidebar from '../EventHubSidebar'
import EventHubConfirmDialog from '../EventHubConfirmDialog'
import {
    CATEGORY_ICON,
    EVENT_HUB_CANCEL_CONTROL_CLASS,
    EVENT_HUB_CONFIRM_CONTROL_CLASS,
    EVENT_HUB_CONTROL_PAIR_CLASS,
    EVENT_HUB_EDIT_CONTROL_CLASS,
    EVENT_HUB_EDIT_CONTROL_MD_CLASS,
} from '../eventHub.utils'
import EventHubEditButton from './EventHubEditButton'
import EventHubPublishToggle from './EventHubPublishToggle'

const { CATEGORIES } = Constants.EVENT

// Figma input skin (3272:6773…): #f7f7f9 fill, #dbdadf border, 5px radius, 33px tall.
// antd Selects/pickers are restyled through descendant selectors on the section wrapper.
const FIELD_SKIN = [
    '[&_.ant-select]:w-full',
    '[&_.ant-select-selector]:bg-[#f7f7f9]!',
    '[&_.ant-select-selector]:border-[#dbdadf]!',
    '[&_.ant-select-selector]:rounded-[5px]!',
    '[&_.ant-select]:h-[33px]',
    '[&_.ant-picker]:w-full',
    '[&_.ant-picker]:bg-[#f7f7f9]',
    '[&_.ant-picker]:border-[#dbdadf]',
    '[&_.ant-picker]:rounded-[5px]',
    '[&_.ant-picker]:h-[33px]',
].join(' ')

function SectionTitle({ icon, children }) {
    return (
        <div className="flex items-center gap-2 text-[20px] font-medium text-[#272727]">
            {icon}
            <span>{children}</span>
        </div>
    )
}

function Divider() {
    return <div className="h-px w-full border-t border-[#dbdadf]" />
}

/** Slash-format payload EventDetailsEditor has always sent alongside the ISO dates. */
const toDatePayload = (value) => {
    const startISO = value?.[0] || null
    const endISO = value?.[1] || null
    const start = (startISO ? convertToSlashDateTimeFormat(startISO) : '').split(' ')
    const end = (endISO ? convertToSlashDateTimeFormat(endISO) : '').split(' ')
    return {
        start: { day: start[0], hour: start[1], iso: startISO },
        end: { day: end[0], hour: end[1], iso: endISO },
    }
}

/** `_nextEvent` is the populated { _id, name, … } of the presentation, or a bare id elsewhere. */
const nextEventIdOf = (competition) => {
    const next = competition?._nextEvent
    const id = next && typeof next === 'object' ? next._id : next
    return id ? String(id) : null
}

const draftFromState = (competition, presentation) => ({
    date: competition?.date,
    timezone: competition?.timezone,
    country: presentation?.country,
    // AddressAutoCompleteInput emits a whole payload (location + coordinates), so
    // the draft holds it verbatim and stays undefined until the field is touched.
    location: undefined,
    category: presentation?.category,
    // Qualification (mock #edQualOn / #edQualEv): the switch and the chosen next event's id.
    qualifying: Boolean(nextEventIdOf(competition)),
    nextEvent: nextEventIdOf(competition),
})

/**
 * Organizer side panel. Idle state is the public sidebar with a blue pencil in
 * its top-right corner (Figma 3790:20049); the pencil swaps in the form-style
 * panel (Figma "Sidebar" 3272:6766).
 *
 * The form is a draft: fields edit local state and nothing is persisted until ✓
 * (Figma 3790:25507 / 3790:25509), which fires one request per changed field —
 * the same endpoints the old EventPage used. Dates/timezone write the
 * leaderboard doc (competition state), country/location/category the event doc
 * (presentation state). ✗ throws the draft away.
 */
export default function EventHubSettingsSidebar() {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const { t: tHome } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: tTicketing } = useTranslation(TranslationNS.TICKETING)
    const { competition, presentation, canEdit, eventIdentifier, setCompetition, setPresentation, setIsLoading } =
        useEventHub()

    const [draft, setDraft] = useState(null)
    const isEditing = draft !== null
    // The organizer's other events ({ _id, name, eventNumber }), the "Next event" choices.
    const [nextEventOptions, setNextEventOptions] = useState([])
    // Re-link / unlink question (old LinkEvents wording) pending the organizer's answer.
    const [linkConfirm, setLinkConfirm] = useState(null)

    useEffect(() => {
        if (!canEdit || !competition?._id) return undefined
        let active = true
        QualificationsServices.findNextEventOptions(competition._id)
            .then((response) => {
                if (active) setNextEventOptions(response.data?.data?.competitions || [])
            })
            .catch((error) => console.error('/EventHubSettingsSidebar findNextEventOptions - error', error))
        return () => {
            active = false
        }
    }, [canEdit, competition?._id])

    const editField = (key) => (value) => setDraft((previous) => ({ ...previous, [key]: value }))

    const currentNextEvent = nextEventIdOf(competition)
    // The switch alone changes nothing: the link exists once an event is chosen (mock setQual).
    const draftNextEvent = draft?.qualifying && draft.nextEvent ? String(draft.nextEvent) : null
    const nextEventChanged = isEditing && draftNextEvent !== currentNextEvent

    /* Bounds for the two date fields, read in the timezone they are displayed in.
       - start: not before today, except on a competition that already started — 99% of them have,
         and a floor at today would grey out their own saved date, leaving the field unusable.
       - end: not before the start, down to the minute (minDate only rules out whole days). */
    const pickerTz = draft?.timezone || dayjs.tz.guess()
    const inTz = (iso) => dayjs(iso).tz(pickerTz)
    const startDate = draft?.date?.start?.iso ? inTz(draft.date.start.iso) : undefined
    const today = dayjs().tz(pickerTz).startOf('day')
    const startMinDate = startDate?.isBefore(today) ? startDate.startOf('day') : today
    const endMinDate = startDate || today
    const disabledEndTime = (current) => {
        if (!startDate || !current?.isSame(startDate, 'day')) return {}
        const upTo = (bound) => Array.from({ length: bound }, (_, index) => index)
        return {
            disabledHours: () => upTo(startDate.hour()),
            disabledMinutes: (hour) => (hour === startDate.hour() ? upTo(startDate.minute() + 1) : []),
        }
    }

    /* The end field only guards what is picked *in it*: moving the start past an end already set
       would otherwise leave the pair inverted. The end follows, keeping the duration. */
    const changeStart = (iso) => {
        const nextStart = iso ? inTz(iso) : undefined
        const endISO = draft.date?.end?.iso
        const pushedEnd =
            nextStart && endISO && nextStart.isAfter(inTz(endISO))
                ? nextStart
                      .add(startDate ? inTz(endISO).diff(startDate) : 0, 'millisecond')
                      .utc()
                      .format()
                : endISO
        editField('date')(toDatePayload([iso, pushedEnd]))
    }

    const save = async () => {
        // Re-linking or unlinking a previously linked event asks first (old LinkEvents confirmation).
        if (nextEventChanged && currentNextEvent && !linkConfirm) {
            const previousName =
                competition?._nextEvent?.name ||
                nextEventOptions.find((option) => String(option._id) === currentNextEvent)?.name ||
                ''
            const nextName = nextEventOptions.find((option) => String(option._id) === draftNextEvent)?.name || ''
            setLinkConfirm({
                title: draftNextEvent
                    ? tTicketing('Are you sure you want to link this event to {{previousLinkedEvent}} ?', {
                          previousLinkedEvent: nextName,
                      })
                    : tTicketing('Are you sure you want to unlink this event from {{previousLinkedEvent}} ?', {
                          previousLinkedEvent: previousName,
                      }),
            })
            return
        }
        setLinkConfirm(null)

        const pending = [
            {
                changed: draft.date !== competition?.date,
                request: () => EventServices.UpdateEventDate(competition._id, draft.date),
                apply: () => setCompetition((previous) => ({ ...previous, date: draft.date })),
            },
            {
                changed: draft.timezone !== competition?.timezone,
                request: () => EventServices.updateEventTimezone(competition._id, draft.timezone),
                apply: () => setCompetition((previous) => ({ ...previous, timezone: draft.timezone })),
            },
            {
                changed: draft.country !== presentation?.country,
                request: () => EventServices.UpdateEventCountry(competition._id, draft.country),
                apply: () => setPresentation((previous) => ({ ...previous, country: draft.country })),
            },
            {
                changed: draft.location !== undefined,
                request: () => EventServices.UpdateEventLocation(competition._id, draft.location),
                apply: () => setPresentation((previous) => ({ ...previous, ...draft.location })),
            },
            {
                changed: draft.category !== presentation?.category,
                request: () => EventServices.UpdateEventCategory(competition._id, draft.category),
                apply: () => setPresentation((previous) => ({ ...previous, category: draft.category })),
            },
            {
                changed: nextEventChanged,
                request: () => QualificationsServices.updateSelectedNextEvent(competition._id, draftNextEvent),
                apply: () => {
                    // Patch with the option so the public "Qualifying for →" line updates at once, then
                    // re-read the presentation for the populated fields the options lack (publish, buttonLink).
                    const option = nextEventOptions.find((candidate) => String(candidate._id) === draftNextEvent)
                    setCompetition((previous) => ({
                        ...previous,
                        _nextEvent: option
                            ? { _id: option._id, name: option.name, eventNumber: option.eventNumber }
                            : null,
                    }))
                    EventServices.getPresentationByEventIdentifier(eventIdentifier)
                        .then((response) => {
                            const fresh = response.data?.leaderboard
                            if (fresh && '_nextEvent' in fresh) {
                                setCompetition((previous) => ({ ...previous, _nextEvent: fresh._nextEvent ?? null }))
                            }
                        })
                        .catch(() => {})
                },
            },
        ].filter((field) => field.changed)

        if (!pending.length) {
            setDraft(null)
            return
        }

        setIsLoading(true)
        try {
            // Sequential: these all write the same event document, so firing them in
            // parallel could let one response overwrite another's changes.
            for (const field of pending) {
                const result = await field.request()
                if (result.status !== 200) {
                    throw new Error(`unexpected status ${result.status}`)
                }
                field.apply()
            }
            notification.success({ message: t('Saved successfully') })
            setDraft(null)
        } catch (error) {
            // A failure part-way through leaves the earlier fields saved and keeps the
            // form open, so the organizer can see what still needs re-submitting.
            console.error('/EventHubSettingsSidebar save - error', error)
            notification.error({ message: t('Oops, an error occurred !') })
        }
        setIsLoading(false)
    }

    const nextEventSelectOptions = nextEventOptions.map((option) => ({ value: String(option._id), label: option.name }))

    const categoryOptions = CATEGORIES.map((category) => {
        const Icon = CATEGORY_ICON[category]
        return {
            value: category,
            label: (
                <span className="inline-flex items-center gap-2">
                    {Icon && <Icon className="h-3.5 w-3 flex-none" />}
                    {tHome(category)}
                </span>
            ),
        }
    })

    if (!isEditing) {
        return (
            <div className="relative" data-tour="hub-settings">
                <EventHubSidebar />
                {/* Mock `#asideEditBtn`: 30px pencil at (right 18, top 22). */}
                <EventHubEditButton
                    onClick={() => setDraft(draftFromState(competition, presentation))}
                    controlClass={EVENT_HUB_EDIT_CONTROL_MD_CLASS}
                    className="absolute right-[18px] top-[22px]"
                />
            </div>
        )
    }

    return (
        <aside
            data-tour="hub-settings"
            className={`relative flex flex-col gap-4.5 rounded-[10px] border border-[#dbdadf] bg-white p-4.5 text-sm nav:shadow-[0_2px_5px_rgba(0,0,0,0.1)] ${FIELD_SKIN}`}
        >
            {/* ✗ ✓ sit in the first heading row (mock `.org-pop-h .ed-acts`, 27px in a 30px row). */}
            <div className={`${EVENT_HUB_CONTROL_PAIR_CLASS} absolute right-[18px] top-5`}>
                <button
                    type="button"
                    aria-label={t('Cancel')}
                    onClick={() => setDraft(null)}
                    className={`${EVENT_HUB_EDIT_CONTROL_CLASS} ${EVENT_HUB_CANCEL_CONTROL_CLASS}`}
                >
                    <CloseOutlined />
                </button>
                <button
                    type="button"
                    aria-label={t('Save')}
                    onClick={save}
                    className={`${EVENT_HUB_EDIT_CONTROL_CLASS} ${EVENT_HUB_CONFIRM_CONTROL_CLASS}`}
                >
                    <CheckOutlined />
                </button>
            </div>

            {/* Start & end date + timezone */}
            <div className="flex flex-col gap-4.5">
                <SectionTitle icon={<CalendarOutlined />}>{t('Start & end date')}</SectionTitle>
                {/* Two labelled fields rather than one range: the range picker never showed which end
                    was being edited, so organizers kept setting the end date in the start's place. */}
                <label className="flex flex-col gap-1.5 text-[13px] text-[#808080]">
                    {t('Start date')}
                    <DateTimeInput
                        id="event-start-date-input"
                        tz={draft.timezone}
                        value={draft.date?.start?.iso}
                        onChange={changeStart}
                        allowClear={false}
                        minDate={startMinDate}
                    />
                </label>
                <label className="flex flex-col gap-1.5 text-[13px] text-[#808080]">
                    {t('End date')}
                    <DateTimeInput
                        id="event-end-date-input"
                        tz={draft.timezone}
                        value={draft.date?.end?.iso}
                        onChange={(iso) => editField('date')(toDatePayload([draft.date?.start?.iso, iso]))}
                        allowClear={false}
                        minDate={endMinDate}
                        disabledTime={disabledEndTime}
                    />
                </label>
                <TimezoneSelect
                    id="event-timezone"
                    selected={draft.timezone}
                    onChange={editField('timezone')}
                    allowClear={false}
                />
            </div>

            <Divider />

            {/* Country & Location */}
            <div className="flex flex-col gap-4.5">
                <SectionTitle icon={<EnvironmentOutlined />}>{t('Country & Location')}</SectionTitle>
                <CountryInput id="event-country" value={draft.country} onChange={editField('country')} alwaysOpen />
                <AddressAutoCompleteInput
                    onLocationChange={editField('location')}
                    placeholder={t('Location')}
                    value={draft.location?.location ?? presentation?.location}
                    theme={{
                        variables: {
                            fontFamily: 'inherit',
                            unit: '14px',
                            borderRadius: '5px',
                            colorBackground: '#f7f7f9',
                            border: '1px solid #dbdadf',
                            boxShadow: 'none',
                        },
                    }}
                />
            </div>

            <Divider />

            {/* Category */}
            <div className="flex flex-col gap-4.5">
                <SectionTitle icon={<AppstoreOutlined />}>{t('Category')}</SectionTitle>
                <Select
                    id="event-category"
                    value={draft.category}
                    disabled={presentation?.category === 'Workshop'}
                    onChange={editField('category')}
                    options={categoryOptions}
                />
            </div>

            <Divider />

            {/* Qualification (mock event.html #edQualOn / #edQualEv): is this event a qualifying phase
                for one of the organizer's other events? The public side panel then shows "Qualifying for →". */}
            <div className="flex flex-col gap-4.5">
                <SectionTitle icon={<TrophyOutlined />}>{t('Qualification')}</SectionTitle>
                <EventHubPublishToggle
                    label={t('This event is a qualifying phase for a next event')}
                    labelClassName="whitespace-normal"
                    className="justify-between"
                    checked={Boolean(draft.qualifying)}
                    onChange={(checked) =>
                        setDraft((previous) => ({
                            ...previous,
                            qualifying: checked,
                            nextEvent: checked ? previous.nextEvent : null,
                        }))
                    }
                />
                {draft.qualifying && (
                    <Select
                        id="event-next-event"
                        aria-label={t('Next event')}
                        placeholder={t('Next event')}
                        value={draft.nextEvent ? String(draft.nextEvent) : undefined}
                        onChange={editField('nextEvent')}
                        options={nextEventSelectOptions}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                    />
                )}
            </div>

            <Divider />

            {/* Event type (mock event.html #edType) — read-only: the API has no endpoint
                to change a competition's type after creation. */}
            <div className="flex flex-col gap-4.5">
                <SectionTitle icon={<GlobalOutlined />}>{t('Event type')}</SectionTitle>
                <Select
                    id="event-type"
                    value={competition?.type === 'online' ? 'online' : 'inside'}
                    disabled
                    options={[
                        { value: 'inside', label: t('Physical') },
                        { value: 'online', label: t('Online') },
                    ]}
                />
            </div>

            {linkConfirm && (
                <EventHubConfirmDialog
                    title={linkConfirm.title}
                    tone="primary"
                    confirmLabel={t('Save')}
                    cancelLabel={t('Cancel')}
                    onCancel={() => setLinkConfirm(null)}
                    onConfirm={save}
                />
            )}
        </aside>
    )
}
